// One rendered page: the raster canvas plus every overlay (annotations,
// widgets, search hits, inspector boxes, live tool previews, content-
// edit input). All geometry is fitz page space scaled by `scale`.
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { canRotate, annotBounds, pointsToPath } from "../lib/annot-geometry.ts";
import {
  buildSnapTargets,
  computeSnap,
  type SnapTargets,
} from "../lib/snap.ts";
import { INSPECT_COLORS } from "../lib/color-utils.ts";
import type { PdfEngine } from "../lib/engine.ts";
import { isCustomFamily, isLibraryFamily } from "../lib/fonts.ts";
import {
  isMarkup,
  NOTE_SIZE,
  quadBounds,
  type Annotation,
  type FillInfo,
  type FormValues,
  type InkAnnotation,
  type LinkAnnotation,
  type PageInfo,
  type Quad,
  type RegionItem,
  type ShapeAnnotation,
  type ShapeKind,
  type WidgetInfo,
} from "../lib/protocol.ts";
import { sampleRasterBackground } from "../lib/raster.ts";
import { regionLabel } from "../lib/selection.ts";
import {
  MeasureFormatContext,
  makeFormatLength,
} from "../lib/measure-format.ts";
import { base14Css, base14Name, familyOfBase14 } from "../lib/text-style.ts";
import {
  isMarkupTool,
  isShapeTool,
  type ActiveTextEdit,
  type DragPhase,
  type Tool,
  type ToolStyles,
} from "../lib/ui-types.ts";
import {
  InkView,
  LinkView,
  MarkupQuadsView,
  NoteView,
  RedactView,
  ShapeView,
  StampView,
  RotateHandle,
  TextAnnotView,
} from "./annot-views.tsx";
import { HtmlBoxDims } from "./dim-labels.tsx";
import {
  FillSelView,
  FormWidgetView,
  ImageSelView,
  RegionSelView,
  TextEditHandles,
} from "./content-sel-views.tsx";

/** How long a finger has to rest before the press becomes a context
 *  menu. Long enough not to fire on a slow tap, short enough that you
 *  don't wonder whether it's working. */
const LONG_PRESS_MS = 500;

/** Movement (client px) that turns a long press into a drag instead. */
const LONG_PRESS_SLOP = 8;

// ─────────────────────────── page canvas ───────────────────────────

/** Lazily renders one page raster; frees it again when scrolled far away. */
export function usePageRaster(
  engine: PdfEngine | null,
  pageIndex: number,
  scale: number,
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  canvasRef: React.RefObject<HTMLCanvasElement | null>
): void {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry?.isIntersecting ?? false),
      { rootMargin: "150% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [wrapperRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !engine) return;
    if (!visible) {
      // Free the raster — a 200-page document at retina resolution
      // would otherwise hold gigabytes of canvas memory.
      canvas.width = 0;
      canvas.height = 0;
      return;
    }
    let cancelled = false;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    void engine
      .render(pageIndex, scale * dpr)
      .then(({ width, height, pixels }) => {
        if (cancelled || !canvasRef.current) return;
        const c = canvasRef.current;
        c.width = width;
        c.height = height;
        const ctx = c.getContext("2d");
        if (!ctx) return;
        ctx.putImageData(
          new ImageData(new Uint8ClampedArray(pixels), width, height),
          0,
          0
        );
      })
      .catch(() => {
        /* stale render after doc close — ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [engine, pageIndex, scale, visible, canvasRef]);
}

// ─────────────────────────── page view ───────────────────────────

export interface PageViewProps {
  engine: PdfEngine | null;
  pageIndex: number;
  info: PageInfo;
  scale: number;
  tool: Tool;
  styles: ToolStyles;
  annots: Annotation[];
  /** Typeface id → CSS family for text boxes (helv/times/cour and any
   *  loaded session faces). */
  annotFontCss?: Record<string, string>;
  /** Turn the selection by `deg` (a drag reports increments). */
  onRotateSelection?: (deg: number, phase: DragPhase) => void;
  /** Move/resize a form field's widget (index within this page). */
  onMoveField?: (
    pageIndex: number,
    widgetIndex: number,
    rect: [number, number, number, number],
    phase: DragPhase
  ) => void;
  selectedIds: ReadonlySet<string>;
  editingId: string | null;
  /** Search matches on this page (quads + whether it's the active hit). */
  searchHits?: { quads: Quad[]; current: boolean }[];
  /** Inspector mode: outline every object with its bounding box. */
  inspect: boolean;
  /** AcroForm fields on this page. */
  widgets?: WidgetInfo[];
  formValues: FormValues;
  onSetFormValue: (name: string, value: string) => void;
  registerEl: (index: number, el: HTMLDivElement | null) => void;
  onCreateMarkup: (pageIndex: number, quads: Quad[]) => void;
  onCreateText: (pageIndex: number, x: number, y: number) => void;
  onCreateInk: (pageIndex: number, points: number[]) => void;
  onCreateShape: (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => void;
  onCreateNote: (pageIndex: number, x: number, y: number) => void;
  onCreateRedact: (pageIndex: number, quads: Quad[]) => void;
  onPlaceStamp: (pageIndex: number, x: number, y: number) => void;
  /** A plain press with the pointer. Content text opens its editor
   *  straight away; other objects select, and press-again opens whatever
   *  editor they have. `mode` is how much text an edit takes (Shift
   *  widens a line to the whole paragraph). */
  onTapAt: (pageIndex: number, x: number, y: number, mode: "line" | "block") => void;
  textEdit?: ActiveTextEdit;
  /** Loaded CSS family for a library/uploaded edit face (WYSIWYG). */
  editFontCss?: string;
  onCommitTextEdit: (value: string) => void;
  onCancelTextEdit: () => void;
  onPatchTextEdit: (
    patch: Partial<Pick<ActiveTextEdit, "moveDx" | "moveDy" | "size">>
  ) => void;
  /** Selected content image on THIS page. */
  imageSel?: { bbox: [number, number, number, number] };
  /** Selected vector fill block on THIS page. */
  fillSel?: { fill: FillInfo };
  /** Committed select-mode text selection on THIS page (Ctrl+C copies). */
  textSel?: { quads: Quad[] };
  onTextSel: (sel: { pageIndex: number; quads: Quad[]; text: string } | null) => void;
  opBusy: boolean;
  onImageMove: (to: [number, number, number, number]) => void;
  onImageDelete: () => void;
  onImageReplaceRequest: () => void;
  onFillMove: (to: [number, number, number, number]) => void;
  onFillDelete: () => void;
  onFillRecolor: (hex: string) => void;
  onFillRestack: (front: boolean) => void;
  onGradientStops: (stops: { start?: string; end?: string }) => void;
  onGradientReverse: () => void;
  onCreateBgBlock: (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => void;
  onCreateLink: (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => void;
  onCropRegion: (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => void;
  onCreateFieldRegion: (pageIndex: number, x1: number, y1: number, x2: number, y2: number) => void;
  /** Multi-object selection on THIS page. */
  regionSel?: { items: RegionItem[] };
  onRegionMove: (dx: number, dy: number) => void;
  /** Un-committed arrow-key nudge of that selection. */
  regionNudge?: [number, number];
  /** Click inside the frame without dragging: collapse to one object. */
  onRegionTapAt?: (pageIndex: number, x: number, y: number) => void;
  /** Modifier+click: add/remove the object under the point in the
   *  multi-selection — annotation, field or page content alike. */
  onToggleObjectAt?: (pageIndex: number, x: number, y: number) => void;
  onFollowLink: (a: LinkAnnotation) => void;
  onEditLink: (a: LinkAnnotation) => void;
  onSelectAnnot: (id: string | null, additive?: boolean) => void;
  /** Marquee result: rect in page space. `additive` extends the current
   *  selection (modifier drag) rather than replacing it (plain drag). */
  onMarqueeSelect?: (
    pageIndex: number,
    rect: [number, number, number, number],
    additive: boolean
  ) => void;
  /** Right-click: page coords + client coords for menu placement. */
  onContextMenuAt?: (
    pageIndex: number,
    x: number,
    y: number,
    clientX: number,
    clientY: number
  ) => void;
  /** Active snap guides while dragging annotations on THIS page. */
  guides?: { v: number | null; h: number | null };
  /** Measure tool: display unit + calibration factor (real ÷ drawn). */
  measureUnit?: "pt" | "mm" | "cm" | "in";
  measureFactor?: number;
  /** Reports each completed measurement's raw length in points. */
  onMeasure?: (lengthPt: number) => void;
  onStartEdit: (id: string) => void;
  onCommitText: (id: string, value: string, width: number, height: number) => void;
  /** Live text-box value on every keystroke (see TextAnnotView). */
  onInputText: (id: string, value: string, width: number, height: number) => void;
  onCommitNote: (id: string, value: string) => void;
  onMoveText: (id: string, x: number, y: number, phase: DragPhase) => void;
  onMeasureText: (id: string, width: number, height: number) => void;
  onDragAnnot: (id: string, dx: number, dy: number, phase: DragPhase) => void;
  onResizeShape: (id: string, end: 1 | 2, dx: number, dy: number, phase: DragPhase) => void;
  onResizeStamp: (id: string, dx: number, dy: number, phase: DragPhase) => void;
}

export const PageView = memo(function PageView({
  engine,
  pageIndex,
  info,
  scale,
  tool,
  styles,
  annots,
  annotFontCss,
  onRotateSelection,
  onMoveField,
  selectedIds,
  editingId,
  searchHits,
  inspect,
  widgets,
  formValues,
  onSetFormValue,
  registerEl,
  onCreateMarkup,
  onCreateText,
  onCreateInk,
  onCreateShape,
  onCreateNote,
  onCreateRedact,
  onPlaceStamp,
  onTapAt,
  textEdit,
  editFontCss,
  onCommitTextEdit,
  onCancelTextEdit,
  onPatchTextEdit,
  imageSel,
  fillSel,
  textSel,
  onTextSel,
  opBusy,
  onImageMove,
  onImageDelete,
  onImageReplaceRequest,
  onFillMove,
  onFillDelete,
  onFillRecolor,
  onFillRestack,
  onGradientStops,
  onGradientReverse,
  onCreateBgBlock,
  onCreateLink,
  onCropRegion,
  onCreateFieldRegion,
  regionSel,
  onRegionMove,
  regionNudge,
  onRegionTapAt,
  onToggleObjectAt,
  onFollowLink,
  onEditLink,
  onSelectAnnot,
  onMarqueeSelect,
  onContextMenuAt,
  guides,
  measureUnit = "mm",
  measureFactor = 1,
  onMeasure,
  onStartEdit,
  onCommitText,
  onInputText,
  onCommitNote,
  onMoveText,
  onMeasureText,
  onDragAnnot,
  onResizeShape,
  onResizeStamp,
}: PageViewProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  usePageRaster(engine, pageIndex, scale, wrapperRef, canvasRef);

  useEffect(() => {
    registerEl(pageIndex, wrapperRef.current);
    return () => registerEl(pageIndex, null);
  }, [pageIndex, registerEl]);

  // Live text-selection preview while the highlight tool drags. The
  // engine maps the two anchor points to glyph quads (async, so track
  // in-flight state and only apply the latest response).
  const [previewQuads, setPreviewQuads] = useState<Quad[] | null>(null);
  const sel = useRef<{
    start: [number, number];
    last: [number, number];
    inFlight: boolean;
    token: number;
  } | null>(null);

  // WYSIWYG editing: load the document's embedded font into the page
  // so the edit overlay renders the real face. Missing glyphs (subset
  // gaps, stripped cmaps) fall through per-glyph to the Base-14 stack.
  const [docFontFamily, setDocFontFamily] = useState<string | null>(null);
  useEffect(() => {
    setDocFontFamily(null);
    if (!textEdit?.fontBytes || typeof FontFace === "undefined") return;
    const family = `pe-doc-font-${textEdit.uid}`;
    let face: FontFace;
    try {
      face = new FontFace(family, textEdit.fontBytes as BufferSource);
    } catch {
      return;
    }
    let cancelled = false;
    face
      .load()
      .then(() => {
        if (cancelled) return;
        document.fonts.add(face);
        setDocFontFamily(family);
      })
      .catch(() => {
        /* CFF/Type1 files the browser can't parse — stack fallback */
      });
    return () => {
      cancelled = true;
      document.fonts.delete(face);
    };
  }, [textEdit]);

  // Select-mode click-vs-drag disambiguation (client px at press).
  const selClick = useRef<{ x: number; y: number } | null>(null);

  // In-progress ink stroke / shape drag (page space, this page only).
  const inkRef = useRef<number[] | null>(null);
  const [inkDraft, setInkDraft] = useState<number[] | null>(null);
  const shapeRef = useRef<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [shapeDraft, setShapeDraft] = useState<typeof shapeRef.current>(null);
  // Marquee drag (pointer mode). `additive` records whether the press
  // started with a modifier: a plain marquee replaces the selection, a
  // modifier one extends it.
  const marqueeRef = useRef<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    additive: boolean;
  } | null>(null);
  const [marqueeDraft, setMarqueeDraft] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);
  /** Did this drag START on a line of text? That — not what happens to
   *  fall between the two anchors — is what separates "select this text"
   *  from "marquee these objects". Dragging out of a paragraph means the
   *  former; dragging from whitespace or a margin means the latter, which
   *  is how every PDF editor behaves and the only rule that lets ONE
   *  plain drag serve both jobs on a page that is mostly text.
   *
   *  Resolved asynchronously from the press, so it is a ref: the first
   *  frame or two of a drag may not know yet, which costs nothing. */
  const dragStartOnText = useRef(false);
  // Content-drag snapping (Edit-tool image/fill/region frames): the
  // targets build lazily at drag start, guides render locally through
  // the same slot the route's annotation-drag guides use.
  const contentSnapCtx = useRef<SnapTargets | null>(null);
  const [contentGuides, setContentGuides] = useState<{
    v: number | null;
    h: number | null;
  } | null>(null);
  const snapContentDrag = useCallback(
    (
      bbox: [number, number, number, number],
      phase: DragPhase
    ): { dx: number; dy: number } => {
      if (phase === "start" || !contentSnapCtx.current) {
        contentSnapCtx.current = buildSnapTargets(
          annots,
          pageIndex,
          new Set(),
          info.width,
          info.height
        );
      }
      const r = computeSnap(bbox, contentSnapCtx.current, 6 / scale);
      if (phase === "end" || phase === "cancel") {
        contentSnapCtx.current = null;
        setContentGuides(null);
      } else {
        setContentGuides(
          r.vGuide !== null || r.hGuide !== null ? { v: r.vGuide, h: r.hGuide } : null
        );
      }
      return r;
    },
    [annots, pageIndex, info.width, info.height, scale]
  );

  // Measure tool: the drag AND the finished measurement (it stays
  // visible until the next drag or a tool change).
  const measureRef = useRef<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [measureDraft, setMeasureDraft] = useState<typeof measureRef.current>(null);
  useEffect(() => {
    if (tool !== "measure") {
      measureRef.current = null;
      setMeasureDraft(null);
    }
  }, [tool]);

  const toPageCoords = useCallback(
    (e: ReactPointerEvent): [number, number] => {
      const rect = wrapperRef.current!.getBoundingClientRect();
      return [(e.clientX - rect.left) / scale, (e.clientY - rect.top) / scale];
    },
    [scale]
  );

  const toClampedCoords = useCallback(
    (e: ReactPointerEvent): [number, number] => {
      const [x, y] = toPageCoords(e);
      return [
        Math.min(Math.max(x, 0), info.width),
        Math.min(Math.max(y, 0), info.height),
      ];
    },
    [toPageCoords, info.width, info.height]
  );

  const requestSelection = useCallback(
    (point: [number, number]) => {
      const s = sel.current;
      if (!s || !engine) return;
      s.last = point;
      if (s.inFlight) return; // latest point is picked up when this one lands
      s.inFlight = true;
      const token = s.token;
      void engine
        .selection(pageIndex, s.start, s.last)
        .then(({ quads }) => {
          if (sel.current?.token === token) setPreviewQuads(quads);
        })
        .catch(() => {})
        .finally(() => {
          const cur = sel.current;
          if (cur && cur.token === token) {
            cur.inFlight = false;
            // Coalesced pointermoves: catch up to the newest point.
            if (cur.last !== point) requestSelection(cur.last);
          }
        });
    },
    [engine, pageIndex]
  );

  // Long-press = right-click, for fingers. A finger has no second
  // button, so the context menu — which already exists and already takes
  // page coordinates — was simply unreachable on touch. Firing marks the
  // press consumed so the pointer-up that follows doesn't ALSO select.
  const longPress = useRef<{
    timer: number;
    fired: boolean;
    x: number;
    y: number;
  } | null>(null);
  const cancelLongPress = useCallback(() => {
    if (longPress.current) window.clearTimeout(longPress.current.timer);
    longPress.current = null;
  }, []);
  const startLongPress = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      cancelLongPress();
      if (e.pointerType !== "touch" || !onContextMenuAt) return;
      const [x, y] = toClampedCoords(e);
      const { clientX, clientY } = e;
      const timer = window.setTimeout(() => {
        if (!longPress.current) return;
        longPress.current.fired = true;
        onContextMenuAt(pageIndex, x, y, clientX, clientY);
      }, LONG_PRESS_MS);
      longPress.current = { timer, fired: false, x: clientX, y: clientY };
    },
    [cancelLongPress, onContextMenuAt, pageIndex, toClampedCoords]
  );
  /** True when the press that is ending was already spent on the menu. */
  const consumedByLongPress = useCallback(() => {
    const fired = longPress.current?.fired ?? false;
    cancelLongPress();
    return fired;
  }, [cancelLongPress]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      // Hand tool: no preventDefault, no pointer capture — the press
      // bubbles to the scroll container, which turns it into a pan.
      if (tool === "hand") return;
      if (isMarkupTool(tool)) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        sel.current = {
          start: toPageCoords(e),
          last: toPageCoords(e),
          inFlight: false,
          token: (sel.current?.token ?? 0) + 1,
        };
        setPreviewQuads(null);
        return;
      }
      if (tool === "text") {
        // preventDefault suppresses the browser's post-mousedown focus
        // fixup — without it, the contentEditable box created (and
        // focused) during this event is immediately blurred to <body>,
        // commits as empty, and deletes itself.
        e.preventDefault();
        const [x, y] = toPageCoords(e);
        onCreateText(pageIndex, x, y);
        return;
      }
      if (tool === "ink") {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const [x, y] = toClampedCoords(e);
        inkRef.current = [x, y];
        setInkDraft([x, y]);
        return;
      }
      if (tool === "redact") {
        // Track a text selection AND the raw drag rect — whichever
        // yields something wins at pointer-up (text quads preferred).
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        sel.current = {
          start: toPageCoords(e),
          last: toPageCoords(e),
          inFlight: false,
          token: (sel.current?.token ?? 0) + 1,
        };
        setPreviewQuads(null);
        const [x, y] = toClampedCoords(e);
        shapeRef.current = { x1: x, y1: y, x2: x, y2: y };
        setShapeDraft(shapeRef.current);
        return;
      }
      if (
        isShapeTool(tool) ||
        tool === "whiteout" ||
        tool === "bgblock" ||
        tool === "link" ||
        tool === "crop" ||
        tool === "field"
      ) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const [x, y] = toClampedCoords(e);
        shapeRef.current = { x1: x, y1: y, x2: x, y2: y };
        setShapeDraft(shapeRef.current);
        return;
      }
      if (tool === "note") {
        // Same focus fixup as text: the note's editor opens focused.
        e.preventDefault();
        const [x, y] = toClampedCoords(e);
        onCreateNote(
          pageIndex,
          Math.min(Math.max(x - NOTE_SIZE / 2, 0), info.width - NOTE_SIZE),
          Math.min(Math.max(y - NOTE_SIZE / 2, 0), info.height - NOTE_SIZE)
        );
        return;
      }
      if (tool === "stamp") {
        e.preventDefault();
        const [x, y] = toClampedCoords(e);
        onPlaceStamp(pageIndex, x, y);
        return;
      }
      if (tool === "measure") {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const [x, y] = toClampedCoords(e);
        measureRef.current = { x1: x, y1: y, x2: x, y2: y };
        setMeasureDraft(measureRef.current);
        return;
      }
      // The pointer. One press, several meanings, all resolved at
      // pointer-up so the gesture decides which:
      //   plain drag over text  → select that text
      //   plain drag elsewhere  → marquee everything in the rect
      //   modifier+drag         → marquee, extending the selection
      //   plain press           → act on whatever is under it
      //
      // A plain drag tracks BOTH a text selection and a marquee rect and
      // picks the winner at pointer-up (text wins when the engine found
      // glyphs) — the same either/or the redact tool uses. Requiring a
      // modifier for the marquee, as this briefly did, means a drag
      // across empty page does nothing at all.
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      startLongPress(e);
      const additive = e.shiftKey || e.ctrlKey || e.metaKey;
      const [mx, my] = toClampedCoords(e);
      marqueeRef.current = { x1: mx, y1: my, x2: mx, y2: my, additive };
      setMarqueeDraft(additive ? { x1: mx, y1: my, x2: mx, y2: my } : null);
      selClick.current = { x: e.clientX, y: e.clientY };
      dragStartOnText.current = false;
      if (!additive) {
        onTextSel(null);
        const start = toPageCoords(e);
        sel.current = {
          start,
          last: start,
          inFlight: false,
          token: (sel.current?.token ?? 0) + 1,
        };
        setPreviewQuads(null);
        const token = sel.current.token;
        void engine
          ?.textHit(pageIndex, start[0], start[1], "line")
          .then((hit) => {
            if (sel.current?.token === token) dragStartOnText.current = hit.kind === "line";
          })
          .catch(() => {});
      }
    },
    [tool, engine, pageIndex, info.width, info.height, toPageCoords, toClampedCoords, onCreateText, onCreateNote, onPlaceStamp, onSelectAnnot, onTextSel, startLongPress]
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Any real movement means this is a drag, not a resting finger.
      const lp = longPress.current;
      if (
        lp &&
        !lp.fired &&
        (Math.abs(e.clientX - lp.x) > LONG_PRESS_SLOP ||
          Math.abs(e.clientY - lp.y) > LONG_PRESS_SLOP)
      ) {
        cancelLongPress();
      }
      if (isMarkupTool(tool) && sel.current) {
        requestSelection(toPageCoords(e));
        return;
      }
      if (tool === "select" && marqueeRef.current) {
        const g = marqueeRef.current;
        const c = selClick.current;
        const moved =
          !c || Math.abs(e.clientX - c.x) > 4 || Math.abs(e.clientY - c.y) > 4;
        const [x, y] = toClampedCoords(e);
        marqueeRef.current = { ...g, x2: x, y2: y };
        const selectingText = !g.additive && dragStartOnText.current;
        if (selectingText && sel.current && moved) requestSelection(toPageCoords(e));
        // Show only the affordance that will actually apply — two at once
        // reads as a bug. Below the 4-px threshold this is still a click.
        setMarqueeDraft(
          moved && !selectingText ? { x1: g.x1, y1: g.y1, x2: x, y2: y } : null
        );
        return;
      }
      if (tool === "redact" && sel.current) {
        requestSelection(toPageCoords(e));
        if (shapeRef.current) {
          const [x, y] = toClampedCoords(e);
          shapeRef.current = { ...shapeRef.current, x2: x, y2: y };
          setShapeDraft(shapeRef.current);
        }
        return;
      }
      if (tool === "measure" && measureRef.current) {
        const [x, y] = toClampedCoords(e);
        measureRef.current = { ...measureRef.current, x2: x, y2: y };
        setMeasureDraft(measureRef.current);
        return;
      }
      if (tool === "ink" && inkRef.current) {
        const [x, y] = toClampedCoords(e);
        const pts = inkRef.current;
        // Thin the stroke: sub-half-point steps add bytes, not fidelity.
        if (Math.hypot(x - pts[pts.length - 2]!, y - pts[pts.length - 1]!) >= 0.5) {
          pts.push(x, y);
          setInkDraft(pts.slice());
        }
        return;
      }
      if (
        (isShapeTool(tool) ||
          tool === "whiteout" ||
          tool === "bgblock" ||
          tool === "link" ||
          tool === "crop" ||
          tool === "field") &&
        shapeRef.current
      ) {
        const [x, y] = toClampedCoords(e);
        shapeRef.current = { ...shapeRef.current, x2: x, y2: y };
        setShapeDraft(shapeRef.current);
      }
    },
    [tool, toPageCoords, toClampedCoords, requestSelection]
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (isMarkupTool(tool)) {
        if (!sel.current || !engine) return;
        const start = sel.current.start;
        const end = toPageCoords(e);
        sel.current = null;
        setPreviewQuads(null);
        void engine
          .selection(pageIndex, start, end)
          .then(({ quads }) => {
            if (quads.length > 0) onCreateMarkup(pageIndex, quads);
          })
          .catch(() => {});
        return;
      }
      if (tool === "select") {
        const g = marqueeRef.current;
        const s = sel.current;
        const c = selClick.current;
        marqueeRef.current = null;
        sel.current = null;
        selClick.current = null;
        setMarqueeDraft(null);
        setPreviewQuads(null);
        if (!g) return;
        if (consumedByLongPress()) return;
        const moved = c && (Math.abs(e.clientX - c.x) > 4 || Math.abs(e.clientY - c.y) > 4);
        const rect: [number, number, number, number] = [
          Math.min(g.x1, g.x2),
          Math.min(g.y1, g.y2),
          Math.max(g.x1, g.x2),
          Math.max(g.y1, g.y2),
        ];

        if (!moved) {
          const [cx, cy] = toClampedCoords(e);
          // Modifier+click toggles one object in or out of the selection;
          // it also catches presses that missed an annotation's own hit
          // area (a thin stroke, a gap between markup quads).
          if (g.additive) onToggleObjectAt?.(pageIndex, cx, cy);
          else onTapAt(pageIndex, cx, cy, e.shiftKey ? "block" : "line");
          return;
        }

        // A modifier drag is always a marquee — no text-selection race.
        if (g.additive) {
          onMarqueeSelect?.(pageIndex, rect, true);
          return;
        }
        // A plain drag out of a paragraph selects that text; a plain drag
        // from anywhere else marquees the objects in the rect.
        if (!engine || !s || !dragStartOnText.current) {
          onMarqueeSelect?.(pageIndex, rect, false);
          return;
        }
        const end = toPageCoords(e);
        void engine
          .selection(pageIndex, s.start, end)
          .then(({ quads, text }) => {
            if (quads.length > 0) onTextSel({ pageIndex, quads, text });
            else onMarqueeSelect?.(pageIndex, rect, false);
          })
          .catch(() => onMarqueeSelect?.(pageIndex, rect, false));
        return;
      }
      if (tool === "redact") {
        const s = sel.current;
        const g = shapeRef.current;
        sel.current = null;
        setPreviewQuads(null);
        shapeRef.current = null;
        setShapeDraft(null);
        if (!s || !engine) return;
        const end = toPageCoords(e);
        void engine
          .selection(pageIndex, s.start, end)
          .then(({ quads }) => {
            if (quads.length > 0) {
              onCreateRedact(pageIndex, quads);
            } else if (g && Math.abs(g.x2 - g.x1) > 3 && Math.abs(g.y2 - g.y1) > 3) {
              // No text under the drag — redact the area instead.
              const x0 = Math.min(g.x1, g.x2);
              const y0 = Math.min(g.y1, g.y2);
              const x1 = Math.max(g.x1, g.x2);
              const y1 = Math.max(g.y1, g.y2);
              onCreateRedact(pageIndex, [[x0, y0, x1, y0, x0, y1, x1, y1]]);
            }
          })
          .catch(() => {});
        return;
      }
      if (tool === "measure") {
        const g = measureRef.current;
        measureRef.current = null;
        if (g) {
          const len = Math.hypot(g.x2 - g.x1, g.y2 - g.y1);
          if (len < 1) setMeasureDraft(null);
          else onMeasure?.(len);
        }
        return;
      }
      if (tool === "ink") {
        const pts = inkRef.current;
        inkRef.current = null;
        setInkDraft(null);
        if (pts && pts.length >= 4) onCreateInk(pageIndex, pts);
        return;
      }
      if (tool === "bgblock" || tool === "link" || tool === "crop" || tool === "field") {
        const g = shapeRef.current;
        shapeRef.current = null;
        setShapeDraft(null);
        if (g && Math.abs(g.x2 - g.x1) > 3 && Math.abs(g.y2 - g.y1) > 3) {
          (tool === "link"
            ? onCreateLink
            : tool === "crop"
              ? onCropRegion
              : tool === "field"
                ? onCreateFieldRegion
                : onCreateBgBlock)(
            pageIndex,
            g.x1,
            g.y1,
            g.x2,
            g.y2
          );
        }
        return;
      }
      if (isShapeTool(tool) || tool === "whiteout") {
        const g = shapeRef.current;
        shapeRef.current = null;
        setShapeDraft(null);
        if (g && Math.hypot(g.x2 - g.x1, g.y2 - g.y1) > 2) {
          onCreateShape(pageIndex, g.x1, g.y1, g.x2, g.y2);
        }
      }
    },
    [tool, engine, pageIndex, toPageCoords, toClampedCoords, onCreateMarkup, onCreateInk, onCreateShape, onCreateRedact, onCreateBgBlock, onCreateLink, onCropRegion, onCreateFieldRegion, onSelectAnnot, onTapAt, onTextSel, onMarqueeSelect, onToggleObjectAt, consumedByLongPress]
  );

  /** The gesture was taken away mid-drag — a touch the browser reclaimed
   *  for scrolling, a lost capture, an OS gesture. No pointerup follows,
   *  so every in-progress draft has to be dropped here or it stays on
   *  screen and resolves later against a stale start point. */
  const onPointerCancel = useCallback(() => {
    sel.current = null;
    selClick.current = null;
    setPreviewQuads(null);
    inkRef.current = null;
    setInkDraft(null);
    shapeRef.current = null;
    setShapeDraft(null);
    marqueeRef.current = null;
    setMarqueeDraft(null);
    measureRef.current = null;
    setMeasureDraft(null);
    contentSnapCtx.current = null;
    setContentGuides(null);
  }, []);

  const drawables = useMemo(
    () => annots.filter((a): a is InkAnnotation | ShapeAnnotation => a.kind === "ink" || a.kind === "shape"),
    [annots]
  );
  const selectable = tool === "select";

  /** Tools whose gesture IS a drag. Touch has to be told which of the
   *  two meanings a one-finger drag has: with one of these armed the
   *  stroke belongs to the tool, otherwise it belongs to the scroller. */
  const armed =
    isMarkupTool(tool) ||
    isShapeTool(tool) ||
    tool === "ink" ||
    tool === "redact" ||
    tool === "whiteout" ||
    tool === "bgblock" ||
    tool === "link" ||
    tool === "crop" ||
    tool === "field" ||
    tool === "measure";

  // Claim the one-finger drag for the armed tool. It has to be done on
  // touchstart, natively and non-passively: preventDefault on
  // pointerdown is a no-op for touch (the event arrives non-cancelable
  // when the surface can scroll), and React's own touchstart is passive.
  // Two or more fingers fall through untouched, so the document still
  // scrolls and pinches while a tool is armed — which `touch-action`
  // alone cannot express, since it can't count fingers.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !armed) return;
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) e.preventDefault();
    };
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => el.removeEventListener("touchstart", onTouchStart);
  }, [armed]);

  // Inspector data for this page — fetched lazily when the mode turns
  // on; the component remounts on baseline changes (gen in its key), so
  // a plain cache never goes stale.
  const [inspectBoxes, setInspectBoxes] = useState<{
    blocks: [number, number, number, number][];
    lines: [number, number, number, number][];
    images: [number, number, number, number][];
    fills?: { bbox: [number, number, number, number]; stroke: boolean; gradient: boolean }[];
  } | null>(null);
  useEffect(() => {
    if (!inspect || inspectBoxes || !engine) return;
    let cancelled = false;
    void engine
      .pageObjects(pageIndex)
      .then((r) => {
        if (!cancelled) setInspectBoxes(r);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [inspect, inspectBoxes, engine, pageIndex]);

  // What the edited text sits on, read off the live raster (see
  // sampleRasterBackground) — keyed by edit so it samples once.
  const editBg = useMemo(
    () =>
      textEdit
        ? sampleRasterBackground(canvasRef.current, textEdit.bbox, info.width)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [textEdit?.uid, info.width]
  );

  /** Live overlay style honouring the formatting bar's current state. */
  const editorStyle = (edit: ActiveTextEdit): React.CSSProperties => {
    const customFam = isLibraryFamily(edit.family) || isCustomFamily(edit.family);
    const styleChanged =
      edit.family !== "original" ||
      edit.bold !== edit.origBold ||
      edit.italic !== edit.origItalic;
    const root =
      edit.family === "original" || customFam
        ? familyOfBase14(edit.base14)
        : (edit.family as "helv" | "times" | "cour");
    const base = base14Css(styleChanged ? base14Name(root, edit.bold, edit.italic) : edit.base14);
    return {
      fontSize: edit.size * scale,
      color: edit.color,
      ...base,
      fontWeight: edit.bold ? 600 : 400,
      fontStyle: edit.italic ? "italic" : "normal",
      ...(!styleChanged && docFontFamily
        ? { fontFamily: `"${docFontFamily}", ${String(base.fontFamily)}` }
        : null),
      // A library/uploaded face (loaded via FontFace) wins outright.
      ...(customFam && editFontCss ? { fontFamily: editFontCss } : null),
      background: edit.background ?? editBg ?? "#fff",
    };
  };

  // The Measure tool's unit and calibration reach every resize overlay
  // from here, so a dimension label and a measurement of the same edge
  // can never disagree.
  const formatLength = useMemo(
    () => makeFormatLength(measureUnit, measureFactor),
    [measureUnit, measureFactor]
  );

  return (
   <MeasureFormatContext.Provider value={formatLength}>
    <div
      ref={wrapperRef}
      className="pe-page mx-auto"
      data-tool={tool}
      data-armed={armed || undefined}
      data-page-index={pageIndex}
      style={{
        width: Math.floor(info.width * scale),
        height: Math.floor(info.height * scale),
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onContextMenu={
        onContextMenuAt
          ? (e) => {
              e.preventDefault();
              const [x, y] = toPageCoords(e as unknown as ReactPointerEvent);
              onContextMenuAt(pageIndex, x, y, e.clientX, e.clientY);
            }
          : undefined
      }
    >
      <canvas
        ref={canvasRef}
        className="absolute left-0 top-0 z-0"
        style={{
          width: Math.floor(info.width * scale),
          height: Math.floor(info.height * scale),
        }}
      />
      {widgets?.map((w, i) => (
        <FormWidgetView
          key={`${w.name}:${i}`}
          w={w}
          value={formValues[w.name] ?? w.value}
          scale={scale}
          interactive={selectable}
          onChange={onSetFormValue}
          // The Field tool is what puts fields into "arrange" mode; with
          // any other tool they stay ordinary form controls.
          onMoveField={
            tool === "field" && onMoveField
              ? (rect, phase) => onMoveField(pageIndex, i, rect, phase)
              : undefined
          }
        />
      ))}
      {inspect && (
        <div className="pointer-events-none absolute inset-0 z-30">
          {inspectBoxes?.blocks.map((b, i) => (
            <div
              key={`ib${i}`}
              className="pe-inspect-box"
              title="Text block"
              style={{
                left: b[0] * scale - 2,
                top: b[1] * scale - 2,
                width: (b[2] - b[0]) * scale + 4,
                height: (b[3] - b[1]) * scale + 4,
                borderColor: INSPECT_COLORS.block,
                borderStyle: "dashed",
              }}
            />
          ))}
          {inspectBoxes?.lines.map((b, i) => (
            <div
              key={`il${i}`}
              className="pe-inspect-box"
              title="Text line"
              style={{
                left: b[0] * scale,
                top: b[1] * scale,
                width: (b[2] - b[0]) * scale,
                height: (b[3] - b[1]) * scale,
                borderColor: INSPECT_COLORS.line,
              }}
            />
          ))}
          {inspectBoxes?.images.map((b, i) => (
            <div
              key={`ii${i}`}
              className="pe-inspect-box"
              title="Image"
              style={{
                left: b[0] * scale,
                top: b[1] * scale,
                width: (b[2] - b[0]) * scale,
                height: (b[3] - b[1]) * scale,
                borderColor: INSPECT_COLORS.image,
              }}
            />
          ))}
          {inspectBoxes?.fills?.map((f, i) => (
            <div
              key={`if${i}`}
              className="pe-inspect-box"
              title={f.gradient ? "Gradient" : f.stroke ? "Border (stroke)" : "Colour block (fill)"}
              style={{
                left: f.bbox[0] * scale - 1,
                top: f.bbox[1] * scale - 1,
                width: (f.bbox[2] - f.bbox[0]) * scale + 2,
                height: (f.bbox[3] - f.bbox[1]) * scale + 2,
                borderColor: f.gradient ? INSPECT_COLORS.gradient : INSPECT_COLORS.fill,
                borderStyle: f.gradient ? "dotted" : "solid",
              }}
            />
          ))}
          {widgets?.map((w, i) => (
            <div
              key={`iw${i}`}
              className="pe-inspect-box"
              title={`Form field: ${w.name}`}
              style={{
                left: w.rect[0] * scale,
                top: w.rect[1] * scale,
                width: (w.rect[2] - w.rect[0]) * scale,
                height: (w.rect[3] - w.rect[1]) * scale,
                borderColor: INSPECT_COLORS.widget,
              }}
            />
          ))}
          {annots.map((a) => {
            const b = annotBounds(a);
            return (
              <div
                key={`ia${a.id}`}
                className="pe-inspect-box"
                title={`Annotation: ${a.kind}`}
                style={{
                  left: b[0] * scale - 1,
                  top: b[1] * scale - 1,
                  width: (b[2] - b[0]) * scale + 2,
                  height: (b[3] - b[1]) * scale + 2,
                  borderColor: INSPECT_COLORS.annot,
                }}
              />
            );
          })}
        </div>
      )}
      {searchHits?.map((h, i) =>
        h.quads.map((q, j) => {
          const b = quadBounds(q);
          return (
            <div
              key={`hit${i}:${j}`}
              className="pe-search-hit"
              data-current={h.current}
              style={{
                left: b.x * scale,
                top: b.y * scale,
                width: b.width * scale,
                height: b.height * scale,
              }}
            />
          );
        })
      )}
      {annots.map((a) =>
        isMarkup(a) ? (
          <MarkupQuadsView
            key={a.id}
            id={a.id}
            kind={a.kind}
            color={a.color}
            quads={a.quads}
            scale={scale}
            selectable={selectable}
            selected={selectedIds.has(a.id)}
            onSelect={onSelectAnnot}
          />
        ) : null
      )}
      {previewQuads && isMarkupTool(tool) && (
        <MarkupQuadsView
          kind={tool}
          color={styles.markupColor}
          quads={previewQuads}
          scale={scale}
          selectable={false}
          selected={false}
        />
      )}
      {((tool === "select" && previewQuads) || textSel?.quads)
        ? (tool === "select" && previewQuads ? previewQuads : (textSel?.quads ?? [])).map(
            (q, i) => {
              const b = quadBounds(q);
              return (
                <div
                  key={`ts${i}`}
                  className="pe-textsel z-10"
                  style={{
                    left: b.x * scale,
                    top: b.y * scale,
                    width: b.width * scale,
                    height: b.height * scale,
                  }}
                />
              );
            }
          )
        : null}
      {annots.map((a) =>
        a.kind === "redact" ? (
          <RedactView
            key={a.id}
            a={a}
            scale={scale}
            selectable={selectable}
            selected={selectedIds.has(a.id)}
            onSelect={onSelectAnnot}
            onDrag={onDragAnnot}
          />
        ) : null
      )}
      {tool === "redact" && previewQuads && previewQuads.length > 0
        ? previewQuads.map((q, i) => {
            const b = quadBounds(q);
            return (
              <div
                key={`rp:${i}`}
                className="pe-redact z-10"
                style={{
                  left: b.x * scale,
                  top: b.y * scale,
                  width: b.width * scale,
                  height: b.height * scale,
                  pointerEvents: "none",
                }}
              />
            );
          })
        : tool === "redact" && shapeDraft
          ? (() => {
              const x = Math.min(shapeDraft.x1, shapeDraft.x2);
              const y = Math.min(shapeDraft.y1, shapeDraft.y2);
              return (
                <div
                  className="pe-redact z-10"
                  style={{
                    left: x * scale,
                    top: y * scale,
                    width: Math.abs(shapeDraft.x2 - shapeDraft.x1) * scale,
                    height: Math.abs(shapeDraft.y2 - shapeDraft.y1) * scale,
                    pointerEvents: "none",
                  }}
                />
              );
            })()
          : null}
      {(drawables.length > 0 || inkDraft || shapeDraft) && (
        <svg
          className="absolute left-0 top-0 z-10"
          width={Math.floor(info.width * scale)}
          height={Math.floor(info.height * scale)}
          viewBox={`0 0 ${info.width} ${info.height}`}
          style={{ pointerEvents: "none" }}
        >
          {drawables.map((a) =>
            a.kind === "ink" ? (
              <InkView
                key={a.id}
                a={a}
                scale={scale}
                selectable={selectable}
                selected={selectedIds.has(a.id)}
                onSelect={onSelectAnnot}
                onDrag={onDragAnnot}
              />
            ) : (
              <ShapeView
                key={a.id}
                a={a}
                scale={scale}
                selectable={selectable}
                selected={selectedIds.has(a.id)}
                onSelect={onSelectAnnot}
                onDrag={onDragAnnot}
                onResize={onResizeShape}
              />
            )
          )}
          {inkDraft && inkDraft.length >= 4 && (
            <path
              d={pointsToPath(inkDraft)}
              fill="none"
              stroke={styles.inkColor}
              strokeWidth={styles.inkWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {shapeDraft &&
            (isShapeTool(tool) ||
              tool === "whiteout" ||
              tool === "bgblock" ||
              tool === "link" ||
              tool === "crop" ||
              tool === "field") && (
            <ShapeView
              a={{
                id: "draft",
                kind: "shape",
                shape:
                  tool === "whiteout" ||
                  tool === "bgblock" ||
                  tool === "link" ||
                  tool === "crop" ||
                  tool === "field"
                    ? "rect"
                    : (tool as ShapeKind),
                pageIndex,
                ...shapeDraft,
                // The whiteout draft gets a faint border so a white
                // box is visible on a white page while dragging.
                color:
                  tool === "whiteout"
                    ? "#c4c4c4"
                    : tool === "bgblock"
                      ? "#748ffc"
                      : tool === "link"
                        ? "#1c7ed6"
                        : tool === "crop"
                          ? "#e8590c"
                          : tool === "field"
                            ? "#7048e8"
                            : styles.shapeColor,
                strokeWidth:
                  tool === "whiteout" ||
                  tool === "bgblock" ||
                  tool === "link" ||
                  tool === "crop" ||
                  tool === "field"
                    ? 0.75
                    : styles.shapeWidth,
                fill:
                  tool === "whiteout"
                    ? "#ffffff"
                    : tool === "bgblock"
                      ? "#e9ecef"
                      : tool === "link" || tool === "crop"
                        ? null
                        : tool === "field"
                          ? "#e5dbff"
                          : tool === "rect" || tool === "ellipse"
                            ? styles.shapeFill
                            : null,
                dash:
                  tool === "whiteout" || tool === "bgblock"
                    ? "solid"
                    : tool === "link" || tool === "crop" || tool === "field"
                      ? "dashed"
                      : styles.shapeDash,
                headStart: styles.shapeHeadStart,
                headEnd: styles.shapeHeadEnd,
              }}
              scale={scale}
              selectable={false}
              selected={false}
              dims
              onSelect={onSelectAnnot}
              onDrag={onDragAnnot}
              onResize={onResizeShape}
            />
          )}
        </svg>
      )}
      {annots.map((a) =>
        a.kind === "text" ? (
          <TextAnnotView
            key={a.id}
            annot={a}
            scale={scale}
            fontCss={annotFontCss?.[a.font ?? "helv"]}
            tool={tool}
            selected={selectedIds.has(a.id)}
            editing={a.id === editingId}
            onSelect={onSelectAnnot}
            onStartEdit={onStartEdit}
            onCommitText={onCommitText}
            onInputText={onInputText}
            onMove={onMoveText}
            onMeasure={onMeasureText}
          />
        ) : a.kind === "note" ? (
          <NoteView
            key={a.id}
            annot={a}
            scale={scale}
            tool={tool}
            selected={selectedIds.has(a.id)}
            editing={a.id === editingId}
            onSelect={onSelectAnnot}
            onStartEdit={onStartEdit}
            onCommitNote={onCommitNote}
            onDrag={onDragAnnot}
          />
        ) : a.kind === "stamp" ? (
          <StampView
            key={a.id}
            a={a}
            scale={scale}
            tool={tool}
            selected={selectedIds.has(a.id)}
            onSelect={onSelectAnnot}
            onDrag={onDragAnnot}
            onResize={onResizeStamp}
          />
        ) : a.kind === "link" ? (
          <LinkView
            key={a.id}
            a={a}
            scale={scale}
            tool={tool}
            selected={selectedIds.has(a.id)}
            onSelect={onSelectAnnot}
            onFollow={onFollowLink}
            onEdit={onEditLink}
            onDrag={onDragAnnot}
          />
        ) : null
      )}
      {imageSel && (
        <ImageSelView
          bbox={imageSel.bbox}
          scale={scale}
          busy={opBusy}
          snap={snapContentDrag}
          onMove={onImageMove}
          onDelete={onImageDelete}
          onReplace={onImageReplaceRequest}
        />
      )}
      {regionSel && regionSel.items.length > 0 && (
        <RegionSelView
          items={regionSel.items}
          scale={scale}
          busy={opBusy}
          snap={snapContentDrag}
          label={regionLabel(regionSel.items)}
          preview={regionNudge}
          onMove={onRegionMove}
          onTapAt={
            onRegionTapAt ? (x, y) => onRegionTapAt(pageIndex, x, y) : undefined
          }
        />
      )}
      {fillSel && (
        <FillSelView
          fill={fillSel.fill}
          scale={scale}
          busy={opBusy}
          snap={snapContentDrag}
          onMove={onFillMove}
          onDelete={onFillDelete}
          onRecolor={onFillRecolor}
          onRestack={onFillRestack}
          onGradientStops={onGradientStops}
          onGradientReverse={onGradientReverse}
        />
      )}
      {textEdit &&
        !textEdit.block &&
        (() => {
          const k = textEdit.origSize > 0 ? textEdit.size / textEdit.origSize : 1;
          const left = (textEdit.bbox[0] + textEdit.moveDx) * scale - 3;
          const top = (textEdit.bbox[1] + textEdit.moveDy) * scale - 3;
          const width = Math.max((textEdit.bbox[2] - textEdit.bbox[0]) * k * scale + 90, 140);
          const height = (textEdit.bbox[3] - textEdit.bbox[1]) * k * scale + 6;
          return (
            <>
              <input
                key={textEdit.uid}
                className="pe-textedit z-30"
                defaultValue={textEdit.text}
                autoFocus
                spellCheck={false}
                style={{ left, top, width, height, ...editorStyle(textEdit) }}
                title={`Editing text (${(textEdit.rawFont || textEdit.base14).replace(/^[A-Z]{6}\+/, "")}) — Enter applies, Esc cancels, empty deletes the line`}
                onFocus={(e) => e.currentTarget.select()}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onCommitTextEdit(e.currentTarget.value);
                  } else if (e.key === "Escape") {
                    onCancelTextEdit();
                  }
                }}
                onBlur={(e) => {
                  // Focus moving into the formatting bar keeps the edit open.
                  if ((e.relatedTarget as HTMLElement | null)?.closest?.("[data-pe-format-bar]")) {
                    return;
                  }
                  onCommitTextEdit(e.currentTarget.value);
                }}
              />
              <TextEditHandles
                scale={scale}
                edit={textEdit}
                onPatch={onPatchTextEdit}
                left={left}
                top={top}
                width={width}
                height={height}
              />
            </>
          );
        })()}
      {textEdit &&
        textEdit.block &&
        (() => {
          const block = textEdit.block;
          const k = textEdit.origSize > 0 ? textEdit.size / textEdit.origSize : 1;
          const left = (textEdit.bbox[0] + textEdit.moveDx) * scale - 3;
          const top = (textEdit.bbox[1] + textEdit.moveDy) * scale - 3;
          const width = (textEdit.bbox[2] - textEdit.bbox[0]) * k * scale + 12;
          const height = Math.max(
            (textEdit.bbox[3] - textEdit.bbox[1]) * k * scale + block.lineHeight * k * scale + 8,
            60
          );
          return (
            <>
              <textarea
                key={textEdit.uid}
                className="pe-textedit pe-textedit-block z-30"
                defaultValue={textEdit.text}
                autoFocus
                spellCheck={false}
                style={{
                  left,
                  top,
                  width,
                  height,
                  lineHeight: `${block.lineHeight * k * scale}px`,
                  ...editorStyle(textEdit),
                }}
                title={`Editing paragraph (${(textEdit.rawFont || textEdit.base14).replace(/^[A-Z]{6}\+/, "")}) — Ctrl+Enter applies, Esc cancels, empty deletes the paragraph`}
                onFocus={(e) => e.currentTarget.select()}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    onCommitTextEdit(e.currentTarget.value);
                  } else if (e.key === "Escape") {
                    onCancelTextEdit();
                  }
                }}
                onBlur={(e) => {
                  // Focus moving into the formatting bar keeps the edit open.
                  if ((e.relatedTarget as HTMLElement | null)?.closest?.("[data-pe-format-bar]")) {
                    return;
                  }
                  onCommitTextEdit(e.currentTarget.value);
                }}
              />
              <TextEditHandles
                scale={scale}
                edit={textEdit}
                onPatch={onPatchTextEdit}
                left={left}
                top={top}
                width={width}
                height={height}
              />
            </>
          );
        })()}
      {/* Rotate grip for the selection. Its own layer so it sits above
          every annotation view and stays clickable even when the
          selection is a text box or a stamp (which are HTML, not SVG). */}
      {onRotateSelection &&
        selectable &&
        (() => {
          const chosen = annots.filter((a) => selectedIds.has(a.id) && canRotate(a));
          if (chosen.length === 0) return null;
          let x0 = Infinity;
          let y0 = Infinity;
          let x1 = -Infinity;
          let y1 = -Infinity;
          for (const a of chosen) {
            const b = annotBounds(a);
            x0 = Math.min(x0, b[0]);
            y0 = Math.min(y0, b[1]);
            x1 = Math.max(x1, b[2]);
            y1 = Math.max(y1, b[3]);
          }
          if (!Number.isFinite(x0)) return null;
          return (
            <svg
              className="absolute left-0 top-0 z-30"
              width={Math.floor(info.width * scale)}
              height={Math.floor(info.height * scale)}
              viewBox={`0 0 ${info.width} ${info.height}`}
              style={{ pointerEvents: "none" }}
            >
              <RotateHandle
                box={[x0, y0, x1, y1]}
                scale={scale}
                onRotateBy={onRotateSelection}
              />
            </svg>
          );
        })()}
      {marqueeDraft && (
        <div
          className="pointer-events-none absolute z-30"
          style={{
            left: Math.min(marqueeDraft.x1, marqueeDraft.x2) * scale,
            top: Math.min(marqueeDraft.y1, marqueeDraft.y2) * scale,
            width: Math.abs(marqueeDraft.x2 - marqueeDraft.x1) * scale,
            height: Math.abs(marqueeDraft.y2 - marqueeDraft.y1) * scale,
            border: "1.5px dashed var(--color-accent)",
            background: "rgb(66 99 235 / 0.08)",
          }}
        >
          <HtmlBoxDims
            width={Math.abs(marqueeDraft.x2 - marqueeDraft.x1)}
            height={Math.abs(marqueeDraft.y2 - marqueeDraft.y1)}
            scale={scale}
            originX={Math.min(marqueeDraft.x1, marqueeDraft.x2)}
            originY={Math.min(marqueeDraft.y1, marqueeDraft.y2)}
          />
        </div>
      )}
      {measureDraft &&
        (() => {
          const lenPt = Math.hypot(
            measureDraft.x2 - measureDraft.x1,
            measureDraft.y2 - measureDraft.y1
          );
          const label = formatLength(lenPt);
          const midX = ((measureDraft.x1 + measureDraft.x2) / 2) * scale;
          const midY = ((measureDraft.y1 + measureDraft.y2) / 2) * scale;
          return (
            <>
              <svg
                className="pointer-events-none absolute left-0 top-0 z-30"
                width={Math.floor(info.width * scale)}
                height={Math.floor(info.height * scale)}
                viewBox={`0 0 ${info.width} ${info.height}`}
              >
                <line
                  x1={measureDraft.x1}
                  y1={measureDraft.y1}
                  x2={measureDraft.x2}
                  y2={measureDraft.y2}
                  stroke="#e8590c"
                  strokeWidth={1.5 / scale}
                  strokeDasharray={`${4 / scale} ${3 / scale}`}
                />
                {(
                  [
                    [measureDraft.x1, measureDraft.y1],
                    [measureDraft.x2, measureDraft.y2],
                  ] as const
                ).map(([cx, cy], i) => (
                  <circle key={i} cx={cx} cy={cy} r={3 / scale} fill="#e8590c" />
                ))}
              </svg>
              {lenPt > 1 && (
                <div
                  className="pointer-events-none absolute z-30 -translate-x-1/2 rounded border border-[#e8590c] bg-[color:var(--color-bg)] px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-[#e8590c] shadow-sm"
                  style={{ left: midX, top: midY + 10 }}
                >
                  {label}
                </div>
              )}
            </>
          );
        })()}
      {(() => {
        const g = guides ?? contentGuides;
        return g && (g.v !== null || g.h !== null) ? (
          <div className="pointer-events-none absolute inset-0 z-30">
            {g.v !== null && (
              <div
                style={{
                  position: "absolute",
                  left: g.v * scale - 0.5,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: "#f06595",
                }}
              />
            )}
            {g.h !== null && (
              <div
                style={{
                  position: "absolute",
                  top: g.h * scale - 0.5,
                  left: 0,
                  right: 0,
                  height: 1,
                  background: "#f06595",
                }}
              />
            )}
          </div>
        ) : null;
      })()}
    </div>
   </MeasureFormatContext.Provider>
  );
});

